import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const ShopContext = createContext(null);
export const useShop = () => useContext(ShopContext);

const GUEST_KEY = "giftora_guest_cart";
const RECENT_KEY = "giftora_recently_viewed";

const loadGuest = () => {
  try {
    return JSON.parse(localStorage.getItem(GUEST_KEY)) || [];
  } catch {
    return [];
  }
};

const sameCartItem = (a, b) =>
  a.product_id === b.product_id &&
  JSON.stringify(a.personalization || null) === JSON.stringify(b.personalization || null);

export function ShopProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], subtotal: 0, coupon_code: null });
  const [guestItems, setGuestItems] = useState(loadGuest);
  const [wishlistIds, setWishlistIds] = useState([]);
  const [recent, setRecent] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(RECENT_KEY)) || [];
    } catch {
      return [];
    }
  });

  const fetchCart = useCallback(async () => {
    try {
      const { data } = await api.get("/cart");
      setCart(data);
    } catch {
      setCart({ items: [], subtotal: 0, coupon_code: null });
    }
  }, []);

  const fetchWishlist = useCallback(async () => {
    try {
      const { data } = await api.get("/wishlist");
      setWishlistIds(data.items.map((p) => p.id));
    } catch {
      setWishlistIds([]);
    }
  }, []);

  useEffect(() => {
    const sync = async () => {
      if (user) {
        const guest = loadGuest();
        if (guest.length) {
          try {
            await api.post("/cart/merge", { items: guest.map((g) => ({ product_id: g.product_id, qty: g.qty, personalization: g.personalization || null })) });
          } catch {
            /* merge best-effort */
          }
          localStorage.removeItem(GUEST_KEY);
          setGuestItems([]);
        }
        fetchCart();
        fetchWishlist();
      } else if (user === false) {
        setCart({ items: [], subtotal: 0, coupon_code: null });
        setWishlistIds([]);
      }
    };
    sync();
  }, [user, fetchCart, fetchWishlist]);

  const persistGuest = (items) => {
    setGuestItems(items);
    localStorage.setItem(GUEST_KEY, JSON.stringify(items));
  };

  const addToCart = useCallback(
    async (product, qty = 1, personalization = null) => {
      if (user) {
        const { data } = await api.post("/cart/add", { product_id: product.id, qty, personalization });
        setCart(data);
        return data;
      }
      const items = [...guestItems];
      const idx = items.findIndex((i) => i.product_id === product.id && JSON.stringify(i.personalization || null) === JSON.stringify(personalization));
      if (idx >= 0) items[idx] = { ...items[idx], qty: Math.min(items[idx].qty + qty, 20) };
      else items.push({ product_id: product.id, qty, personalization, name: product.name, image: product.image || product.images?.[0], price: product.effective_price || product.price });
      persistGuest(items);
      return null;
    },
    [user, guestItems]
  );

  const updateQty = useCallback(
    async (item, qty) => {
      if (user) {
        const { data } = await api.post("/cart/update", { product_id: item.product_id, qty, personalization: item.personalization || null });
        setCart(data);
      } else {
        const items = guestItems
          .map((i) => (sameCartItem(i, item) ? { ...i, qty } : i))
          .filter((i) => i.qty > 0);
        persistGuest(items);
      }
    },
    [user, guestItems]
  );

  const removeItem = useCallback(
    async (item) => {
      if (user) {
        const { data } = await api.post("/cart/remove", { product_id: item.product_id, qty: 0, personalization: item.personalization || null });
        setCart(data);
      } else {
        persistGuest(guestItems.filter((i) => !sameCartItem(i, item)));
      }
    },
    [user, guestItems]
  );

  const toggleWishlist = useCallback(
    async (productId) => {
      if (!user) return { needsAuth: true };
      const { data } = await api.post("/wishlist/toggle", { product_id: productId, qty: 1 });
      setWishlistIds(data.product_ids);
      return data;
    },
    [user]
  );

  const addRecent = useCallback((product) => {
    setRecent((prev) => {
      const next = [{ id: product.id, name: product.name, image: product.image || product.images?.[0], price: product.effective_price || product.price, original_price: product.original_price, rating: product.rating, review_count: product.review_count }, ...prev.filter((p) => p.id !== product.id)].slice(0, 8);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const displayCart = useMemo(() => {
    if (user) return cart;
    const items = guestItems.map((g) => ({ ...g, line_total: (g.price || 0) * g.qty }));
    return { items, subtotal: items.reduce((s, i) => s + i.line_total, 0), coupon_code: null };
  }, [user, cart, guestItems]);

  const cartCount = displayCart.items.reduce((s, i) => s + i.qty, 0);

  return (
    <ShopContext.Provider
      value={{
        cart: displayCart, cartCount, wishlistIds, recent,
        addToCart, updateQty, removeItem, toggleWishlist, addRecent, fetchCart,
      }}
    >
      {children}
    </ShopContext.Provider>
  );
}
